import { STEFAN_BOLTZMANN, SOLAR_RADIUS, SOLAR_LUMINOSITY, SOLAR_TEMPERATURE } from '../constants';

/**
 * engines/stellar
 *
 * 主系列星の質量光度関係・質量半径関係・有効温度・スペクトル分類・
 * 主系列寿命を扱う。engines/orbital と同様、副作用のない純粋関数のみで構成する。
 *
 * 【重要な注意】
 * ここで使う質量光度関係・質量半径関係は主系列星の「経験的近似式」であり、
 * ケプラーの法則のような厳密な物理法則ではない。実在の恒星は同じ質量でも
 * 年齢・金属量・自転などでL・R・Tにばらつきがある（実測でも数十%のスキャッター
 * が普通にある）。したがって以下の検証テストの許容誤差は engines/orbital より
 * 大幅に緩く設定している。engines/orbital の「0.1%以内」と混同しないこと。
 */

// ============================================================
// 質量光度関係・質量半径関係（区分的な経験式）
// ============================================================

/**
 * 主系列星の質量光度関係。質量 M [太陽質量] から光度 L [太陽光度単位] を推定する。
 *
 * 区分的な近似（一般的な教科書レベルの経験式）:
 *  - M < 0.43:        L = 0.23 M^2.3
 *  - 0.43 <= M < 2:   L = M^4
 *  - 2 <= M < 55:     L = 1.4 M^3.5
 *  - M >= 55:         L = 32000 M
 */
export function luminosityFromMass(massSolar: number): number {
  if (massSolar <= 0) throw new Error('massSolar must be positive');
  if (massSolar < 0.43) return 0.23 * Math.pow(massSolar, 2.3);
  if (massSolar < 2) return Math.pow(massSolar, 4);
  if (massSolar < 55) return 1.4 * Math.pow(massSolar, 3.5);
  return 32000 * massSolar;
}

/**
 * 主系列星の質量半径関係。質量 M [太陽質量] から半径 R [太陽半径単位] を推定する。
 * (Demircan & Kahraman 1991 の経験式を簡略化したもの)
 *
 *  - M < 1:  R = M^0.8
 *  - M >= 1: R = M^0.57
 */
export function radiusFromMass(massSolar: number): number {
  if (massSolar <= 0) throw new Error('massSolar must be positive');
  return massSolar < 1 ? Math.pow(massSolar, 0.8) : Math.pow(massSolar, 0.57);
}

/**
 * シュテファン-ボルツマンの法則の逆算により有効温度を求める。
 * L = 4πR²σT⁴  →  T = (L / (4πR²σ))^(1/4)
 *
 * 入力はSI単位（光度[W]、半径[m]）。太陽単位で使いたい場合は
 * effectiveTemperatureFromSolarUnits を使うこと。
 */
export function effectiveTemperatureK(luminosityW: number, radiusM: number): number {
  if (luminosityW <= 0 || radiusM <= 0) throw new Error('luminosity and radius must be positive');
  return Math.pow(luminosityW / (4 * Math.PI * radiusM * radiusM * STEFAN_BOLTZMANN), 0.25);
}

/** 太陽単位（L in L☉, R in R☉）から有効温度[K]を求める便宜関数。 */
export function effectiveTemperatureFromSolarUnits(luminositySolar: number, radiusSolar: number): number {
  const luminosityW = luminositySolar * SOLAR_LUMINOSITY;
  const radiusM = radiusSolar * SOLAR_RADIUS;
  return effectiveTemperatureK(luminosityW, radiusM);
}

/**
 * 質量[太陽質量]だけから、質量光度関係・質量半径関係・シュテファン-ボルツマン則を
 * 連鎖させて有効温度を推定する一括関数。UIから最もよく呼ばれる想定。
 */
export function stellarPropertiesFromMass(massSolar: number): {
  massSolar: number;
  luminositySolar: number;
  radiusSolar: number;
  temperatureK: number;
} {
  const luminositySolar = luminosityFromMass(massSolar);
  const radiusSolar = radiusFromMass(massSolar);
  const temperatureK = effectiveTemperatureFromSolarUnits(luminositySolar, radiusSolar);
  return { massSolar, luminositySolar, radiusSolar, temperatureK };
}

// ============================================================
// 主系列寿命
// ============================================================

/**
 * 主系列星の寿命 τ [年] を推定する。
 * τ ≈ 10^10 × (M/M☉) / (L/L☉) 年
 * （太陽の主系列寿命を約100億年とする教科書レベルの近似）
 */
export function mainSequenceLifetimeYears(massSolar: number, luminositySolar: number): number {
  return 1e10 * (massSolar / luminositySolar);
}

// ============================================================
// スペクトル分類
// ============================================================

export type SpectralType = 'O' | 'B' | 'A' | 'F' | 'G' | 'K' | 'M';

const SPECTRAL_BOUNDARIES: { type: SpectralType; minTempK: number }[] = [
  { type: 'O', minTempK: 30000 },
  { type: 'B', minTempK: 10000 },
  { type: 'A', minTempK: 7500 },
  { type: 'F', minTempK: 6000 },
  { type: 'G', minTempK: 5200 },
  { type: 'K', minTempK: 3700 },
  { type: 'M', minTempK: 0 },
];

/** 有効温度[K]からハーバード分類のスペクトル型を判定する（O/B/A/F/G/K/Mの粗い区分）。 */
export function spectralTypeFromTemperature(temperatureK: number): SpectralType {
  for (const { type, minTempK } of SPECTRAL_BOUNDARIES) {
    if (temperatureK >= minTempK) return type;
  }
  return 'M';
}

// ============================================================
// HR図上の位置（正規化座標）
// ============================================================

/**
 * HR図表示用に、有効温度と光度を [0,1] の正規化座標に変換する。
 * x=0 が高温側（左端）、x=1 が低温側（右端）となるよう対数スケールで反転する。
 * y=0 が高光度側（上端）、y=1 が低光度側（下端）となるよう対数スケールで反転する。
 *
 * @param logTempRangeK  [最低温度, 最高温度] のタプル（対数軸の両端、既定は3000K〜30000K）
 * @param logLumRange    [最低光度, 最高光度] のタプル（太陽光度単位、対数軸の両端、既定は10^-3〜10^4）
 */
export function hrDiagramPosition(
  temperatureK: number,
  luminositySolar: number,
  logTempRangeK: [number, number] = [3000, 30000],
  logLumRange: [number, number] = [1e-3, 1e4],
): { x: number; y: number } {
  const logTMin = Math.log10(logTempRangeK[0]);
  const logTMax = Math.log10(logTempRangeK[1]);
  const logT = Math.log10(temperatureK);
  const x = (logTMax - logT) / (logTMax - logTMin);

  const logLMin = Math.log10(logLumRange[0]);
  const logLMax = Math.log10(logLumRange[1]);
  const logL = Math.log10(luminositySolar);
  const y = (logLMax - logL) / (logLMax - logLMin);

  return { x: clamp01(x), y: clamp01(y) };
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}
